import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import medallionsImage from "assets/enhancements.svg";
import Tooltip, { TooltipDesc, TooltipName } from "components/base/Tooltip";
import { floatAnimation } from "components/styles/animations";
import { media } from "components/styles/breakpoints";
import AvatarShip from "components/ui/AvatarShip";
import { isFightModeStatus } from "libs/fight";
import { viewPlayerDetails } from "redux/actions";
import { getFightStatus } from "redux/selectors/fightSelectors";
import { OnlinePlayerAvatarModel } from "types";
import { COLORS } from "utils/constants";

interface OnlinePlayerAvatarProps {
  player: OnlinePlayerAvatarModel;
}

interface OnlinePlayerWrapperProps {
  position: number;
  isFaded: boolean;
}

const OnlinePlayerWrapper = styled.div<OnlinePlayerWrapperProps>`
  position: absolute;
  height: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${floatAnimation} 3.5s ease-in-out infinite;
  left: calc(${(props) => props.position * 0.2}% + 20vw);
  transition: all 200ms ease-out;
  z-index: 4;
  pointer-events: auto;
  ${(props) => props.isFaded && "opacity: 0.2;"}

  &:hover {
    filter: brightness(120%);
  }
`;

const InfoWrapper = styled.div`
  position: absolute;
  bottom: 80%;
`;

const NameWrapper = styled.div`
  font-size: 14px;
  font-weight: 400;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 5px;
  gap: 6px;
  text-shadow: 0 0 3px #000000;

  ${media.desktop`
    font-size: 18px;
  `}
`;

const Name = styled.div`
  color: #98d8b1;
`;

const MedallionsWrapper = styled.div`
  z-index: 2;
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const MedallionsInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  color: ${COLORS.ENHANCEMENTS};
`;

const MedallionsIcon = styled.div<{ image: string }>`
  height: 16px;
  width: 16px;
  mask: url(${(props) => props.image}) no-repeat center;
  background-color: ${COLORS.ENHANCEMENTS};
`;

const MedallionsValue = styled.div``;

const AvatarImageWrapper = styled.div`
  height: 100%;
  cursor: pointer;
  position: relative;
`;

export default function OnlinePlayerAvatar({
  player,
}: OnlinePlayerAvatarProps) {
  const dispatch = useDispatch();

  const status = useSelector(getFightStatus);
  const isCurrentPlayerFightMode = isFightModeStatus(status);

  const {
    userId,
    userName,
    ship,
    totalMedallions,
    installedUpgrades,
    position,
  } = player;

  return (
    <OnlinePlayerWrapper
      position={position}
      isFaded={isCurrentPlayerFightMode}
      onClick={() => dispatch(viewPlayerDetails(userId))}
    >
      <InfoWrapper>
        <NameWrapper>
          <Name>{userName}</Name>
          <MedallionsWrapper>
            <Tooltip
              color={COLORS.ENHANCEMENTS}
              content={
                <>
                  <TooltipName>Medallions</TooltipName>
                  <TooltipDesc>{`${userName} currently has ${totalMedallions} total medallions.`}</TooltipDesc>
                </>
              }
            >
              <MedallionsInfo>
                <MedallionsIcon image={medallionsImage} />
                <MedallionsValue>{totalMedallions}</MedallionsValue>
              </MedallionsInfo>
            </Tooltip>
          </MedallionsWrapper>
        </NameWrapper>
      </InfoWrapper>
      <AvatarImageWrapper>
        <AvatarShip ship={ship} installedUpgrades={installedUpgrades} />
      </AvatarImageWrapper>
    </OnlinePlayerWrapper>
  );
}
